/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Cookie from 'js-cookie';
import Ladda from 'ladda';
import moment from 'moment';
import Syphon from 'backbone.syphon';
// const MomentCustomCell = require('psa-backgrid/MomentCustomCell');

import Config from '@config/Config';
import Session from '@/Session';
import makeUploadCollection from '@/util/makeUploadCollection';
import makeFormReadOnly from '@/util/makeFormReadOnly';
import openInNewTab from '@/util/openInNewTab';
import showModal from '@/util/showModal';
import showErrorMessage from '@/util/showErrorMessage';
import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';
import RadioGroupToggleBehavior from '@/behaviors/RadioGroupToggleBehavior';
import ReadOnlyFormBehavior from '@/behaviors/ReadOnlyFormBehavior';
import TaskBehavior from '@/behaviors/TaskBehavior';
import ToggleBehavoir from '@/behaviors/ToggleBehavoir';
import AddressDisplayView from '@/views/AddressDisplayView';
import AddressView from '@/views/AddressView';
import ConfirmationModalView from '@/views/ConfirmationModalView';
import FileUploadView from '@/views/FileUploadView';
import FileUploadTableView from '@/views/FileUploadTableView';
import GridView from '@/views/GridView';
import SelectFieldView from '@/views/SelectFieldView';
import SpinnerView from '@/views/SpinnerView/SpinnerView';
import USStateSelectFieldView from '@/views/USStateSelectFieldView';
import AddressCell from '@/backgrid/AddressCell';
import BooleanCell from '@/backgrid/BooleanCell';
import ButtonCell from '@/backgrid/ButtonCell';
import ButtonLinkCell from '@/backgrid/ButtonLinkCell';
import DateInputCellEditor from '@/backgrid/DateInputCellEditor';
import DollarCell from '@/backgrid/DollarCell';
import DollarInputCellEditor from '@/backgrid/DollarInputCellEditor';
import FileUploadCell from '@/backgrid/FileUploadCell';
import ListCell from '@/backgrid/ListCell';
import MomentFormatter from '@/backgrid/MomentFormatter';
import MultiButtonCell from '@/backgrid/MultiButtonCell';
import ParagraphCell from '@/backgrid/ParagraphCell';
import SelectOneCell from '@/backgrid/SelectOneCell';
import StaticInputCell from '@/backgrid/StaticInputCell';
import StringCell from '@/backgrid/StringCell';
import TruncateStringCell from '@/backgrid/TruncateStringCell';
import RevokeTask from '@/services/RevokeTask';

import IntDashboardSidebarNavView from '../../views/IntDashboardSidebarNavView';

import ExtendedGridView from './ExtendedGridView';
import TASK from './task.json';

const LOREM =
  'Duis tincidunt tincidunt nulla, et pulvinar ante molestie ut.' +
  'In eleifend aliquam aliquam. Mauris non orci dignissim, finibus massa vel, ' +
  'tincidunt dui. In interdum rhoncus quam, id venenatis mi suscipit vel. ' +
  'Nulla facilisi. Donec eget accumsan lacus. Cras in condimentum risus. ' +
  'Orci varius natoque penatibus et magnis dis parturient montes, nascetur ' +
  'ridiculus mus. Pellentesque dictum ligula ac nibh tincidunt, ut ornare ' +
  'enim ultricies. Maecenas non tellus sem.';

const ADDRESS = {
  addressLine1: '123 Main St.',
  addressLine2: 'Apt 1',
  city: 'Fairfax',
  state: 'VA',
  postalCode: '19118',
  country: 'US'
};

const INVOICE_ITEMS = [
  {
    description: 'Test 1',
    quantity: '2',
    unitPrice: '299'
  },
  {
    description: 'Test 2',
    quantity: '3',
    unitPrice: '499'
  }
];

import './TestPage.css';
import tmplTestPage from './TestPage.hbs';

const TestPage = View.extend({
  template: tmplTestPage,

  className: 'test-page sidebar-layout container-fluid',

  ui: {
    sideBar: '.sidebar-nav-container',
    addressDisplayContainer: '.address-display-container',
    addressContainer: '.address-container',
    openConfirmModalBtn: '.open-confirm-modal',
    fileUploadTableContainer: '.file-upload-table-container',
    singleFileContainer: '.single-file-container',
    multiFileContainer: '.multi-file-container',
    invoiceGridContainer: '.invoice-grid-container',
    showAlertBtn: '.show-alert',
    payeezyContainer: '.payeezy-container',
    selectFieldContainer: '.select-field-container',
    spinnerContainer: '.spinner-container',
    usStateContainer: '.us-state-container',
    openNewTabBtn: '.open-new-tab',
    triggerAjaxErrorBtn: '.trigger-ajax-error',
    parsleyTestForm: '.parsley-test-form',
    syphonDeserializeTestForm: '.syphon-deserialize-test-form',
    submitParsleyFormBtn: '.submit-parsley-form',
    makeReadOnlyBtn: '.make-read-only',
    testGrid1Container: '.test-grid-1-container',
    testGrid2Container: '.test-grid-2-container',
    testGrid3Container: '.test-grid-3-container',
    extendedGridContainer: '.extended-grid-container'
  },

  regions: {
    sideBar: '@ui.sideBar',
    addressDisplayContainer: '@ui.addressDisplayContainer',
    addressContainer: '@ui.addressContainer',
    fileUploadTableContainer: '@ui.fileUploadTableContainer',
    singleFileContainer: '@ui.singleFileContainer',
    multiFileContainer: '@ui.multiFileContainer',
    invoiceGridContainer: '@ui.invoiceGridContainer',
    payeezyContainer: '@ui.payeezyContainer',
    selectFieldContainer: '@ui.selectFieldContainer',
    spinnerContainer: '@ui.spinnerContainer',
    usStateContainer: '@ui.usStateContainer',
    testGrid1Container: '@ui.testGrid1Container',
    testGrid2Container: '@ui.testGrid2Container',
    testGrid3Container: '@ui.testGrid3Container',
    extendedGridContainer: '@ui.extendedGridContainer'
  },

  events: {
    'click @ui.openConfirmModalBtn': 'onClickOpenConfirmModal',
    'click @ui.showAlertBtn': 'onClickShowAlert',
    'click @ui.openNewTabBtn': 'onClickOpenNewTab',
    'click @ui.triggerAjaxErrorBtn': 'onClickTriggerAjaxError',
    'click @ui.submitParsleyFormBtn': 'onClickSubmitParsleyForm',
    'click @ui.makeReadOnlyBtn': 'onClickMakeReadOnly'
  },

  behaviors: {
    phone: PhoneFormatBehavior,
    // task: TaskBehavior,
    radioToggle: {
      behaviorClass: RadioGroupToggleBehavior,
      radios: [
        {
          target: '.mailing-address-container',
          radioGroup: 'input[name="isMailingSame"]',
          valueToShow: '0'
        }
      ]
    },
    readOnly: ReadOnlyFormBehavior,
    toggle: {
      behaviorClass: ToggleBehavoir,
      toggles: [
        {
          target: '.reviewer-row',
          group: '[name="actionId"]',
          valuesToShow: '3'
        }
      ]
    }
  },
  onBeforeRender() {
    this.model = new Backbone.Model({
      task: TASK
    });
    this.options.isReadOnly = false;
  },

  onRender() {
    this.showChildView('sideBar', new IntDashboardSidebarNavView());

    /**
     * AddressDisplayView
     */
    this.showChildView(
      'addressDisplayContainer',
      new AddressDisplayView({
        model: new Backbone.Model(ADDRESS)
      })
    );

    /**
     * AddressView
     */
    this.showChildView('addressContainer', new AddressView());

    /**
     * FileUploadTableView
     */
    this.showChildView(
      'fileUploadTableContainer',
      new FileUploadTableView({
        isReadOnly: false,
        emptyText: 'No documents attached',
        documentTypes: [
          {
            value: 'test1',
            display: 'Test 1'
          },
          {
            value: 'test2',
            display: 'Test 2'
          }
        ]
      })
    );

    /**
     * FileUploadView - Single
     */
    this.showChildView(
      'singleFileContainer',
      new FileUploadView({
        collection: makeUploadCollection(),
        isReadOnly: this.options.isReadOnly,
        metadata: {
          documentTypeId: 10019
        }
      })
    );

    /**
     * FileUploadView - Mulitiple
     */
    this.showChildView(
      'multiFileContainer',
      new FileUploadView({
        collection: makeUploadCollection(),
        isReadOnly: this.options.isReadOnly,
        allowMultipleFiles: true,
        metadata: {
          documentTypeId: 10019
        }
      })
    );

    /**
     * SelectFieldView
     */
    this.selectField = new SelectFieldView({
      isDisabled: false,
      isRequired: false,
      multiple: true,
      name: 'parentObjectId',
      selectClass: 'parent-license-select',
      collection: new Backbone.Collection([
        {
          value: '1',
          text: 'Foo'
        },
        {
          value: '2',
          text: 'Bar'
        }
      ])
    });
    this.showChildView('selectFieldContainer', this.selectField);
    this.selectField.$el.val(null).select2({
      placeholder: 'Select states'
    });

    /**
     * SpinnerView
     */
    this.showChildView('spinnerContainer', new SpinnerView());

    /**
     * USStateSelectView
     */
    this.stateSelect = new USStateSelectFieldView({
      emptyOption: false,
      name: 'operatingStateCodes',
      isRequired: true,
      multiple: true
    });
    this.showChildView('usStateContainer', this.stateSelect);
    this.stateSelect.$el.val(null).select2({
      placeholder: 'Select states'
    });

    /**
     * Backbone.Syphon Deserialize
     */
    Syphon.deserialize(this.ui.syphonDeserializeTestForm, {
      paidDate: null,
      bondAmount: null
    });

    /**
     * Grid View 1
     */
    this.showChildView(
      'testGrid1Container',
      new GridView({
        columns: [
          {
            name: 'address',
            label: 'Address Cell',
            cell: AddressCell,
            headerCell: 'custom',
            width: 20,
            editable: false,
            sortable: false
          },
          {
            name: 'shouldDelete',
            label: 'Boolean Cell',
            cell: BooleanCell,
            headerCell: 'custom',
            width: 20,
            editable: true,
            sortable: false
          },
          {
            name: 'claim',
            label: 'Button Cell',
            cell: ButtonCell.extend({
              name: 'Start',
              onClick() {
                console.log('test');
              },
              buttonAttrs: {
                'data-style': 'zoom-in',
                'data-color': '#EEEEEE',
                'data-spinner-color': '#EEEEEE'
              },
              buttonClass: 'btn-primary btn-sm ladda-button',
              labelClass: 'ladda-label'
            }),
            headerCell: 'custom',
            width: 20,
            editable: false
          },
          {
            name: '',
            label: 'Button Link Cell',
            cell: ButtonLinkCell.extend({
              name: 'Open',
              buttonClass: 'btn-sm btn-outline-primary',
              href(model) {
                const individualId = model.get('individualId');
                return `#conser/individual/${individualId}/dashboard`;
              }
            }),
            headerCell: 'custom',
            width: 20,
            editable: false
          },
          {
            name: 'amount',
            label: 'Dollar Cell',
            cell: DollarCell,
            headerCell: 'custom',
            width: 20,
            editable: false
          }
        ],
        collection: new Backbone.Collection([
          {
            amount: 1234.34,
            shouldDelete: true,
            address: ADDRESS
          }
        ])
      })
    );

    /**
     * Grid View 2
     */
    this.showChildView(
      'testGrid2Container',
      new GridView({
        columns: [
          {
            name: 'fictitiousNameDocument',
            label: 'File Upload Cell',
            cell: FileUploadCell.extend({
              allowMultipleFiles: false,
              isReadOnly: false,
              metadata: {
                documentTypeId: 10017
              }
            }),
            width: 33,
            headerCell: 'custom'
          },
          {
            name: 'licensesList',
            label: 'List Cell',
            cell: ListCell,
            headerCell: 'custom',
            width: 20,
            editable: false
          },
          // {
          //   name: 'deliveryDate',
          //   label: 'Moment Cell',
          //   formatter: MomentFormatter,
          //   cell: MomentCustomCell.MomentCustomCell,
          //   headerCell: 'custom',
          //   width: 15,
          //   editable: false,
          // },
          {
            name: '',
            label: 'Multi Button Cell',
            cell: MultiButtonCell.extend({
              buttons: [
                {
                  name: 'Open',
                  buttonClass: 'btn-primary btn-sm',
                  href(model) {
                    return '#nondep/test';
                  }
                },
                {
                  name: 'Delete',
                  buttonClass: 'btn-danger btn-sm ladda-button',
                  onClick() {
                    console.log('Delete!');
                  }
                }
              ]
            }),
            headerCell: 'custom',
            width: 20,
            editable: false
          },
          {
            name: 'comments',
            label: 'Paragraph Cell',
            cell: ParagraphCell,
            headerCell: 'custom',
            width: 27,
            editable: false,
            sortable: false
          }
        ],
        collection: new Backbone.Collection([
          {
            licensesList: [
              {
                value: 'Bar2',
                label: 'Foo2'
              },
              {
                value: 'Bar1',
                label: 'Foo1'
              }
            ],
            deliveryDate: moment(),
            comments: LOREM
          }
        ])
      })
    );

    /**
     * Grid View 3
     */
    this.showChildView(
      'testGrid3Container',
      new GridView({
        columns: [
          {
            name: 'selected',
            label: 'Select One Cell',
            cell: SelectOneCell.SelectOneRowCell,
            headerCell: SelectOneCell.SelectOneHeaderCell,
            width: 3,
            editable: true
          },
          {
            name: 'nextStepStatus',
            label: 'Truncate String Cell',
            cell: TruncateStringCell,
            headerCell: 'custom',
            width: 25,
            editable: false
          },
          {
            name: 'entityName',
            label: 'Entity Name',
            cell: StringCell,
            headerCell: 'custom',
            width: 20,
            editable: false
          },
          {
            name: 'newAssetsAmount',
            label: 'StaticInputCell - Dollar',
            cell: StaticInputCell,
            editor: DollarInputCellEditor.extend({
              placeholder: 'New Assets Amount'
            }),
            headerCell: 'custom',
            width: 20,
            sortable: false
          },
          {
            name: 'newAssetsAsOfDate',
            label: 'StaticInputCell - Date',
            cell: StaticInputCell,
            editor: DateInputCellEditor.extend({
              placeholder: 'New As of Date'
            }),
            headerCell: 'custom',
            width: 20,
            sortable: false
          }
        ],
        collection: new Backbone.Collection([
          {
            nextStepStatus: LOREM,
            entityName: null,
            newAssetsAmount: '1000',
            newAssetsAsOfDate: '12/20/2020'
          },
          {
            nextStepStatus: LOREM,
            entityName: 'xyz'
          },
          {
            nextStepStatus: LOREM,
            entityName: 'abc'
          }
        ])
      })
    );

    /**
     *Extende
     */
    this.showChildView(
      'extendedGridContainer',
      new ExtendedGridView({
        model: new Backbone.Model({
          correspondences: [
            {
              filingId: 12345,
              category: 'Some Category',
              title: 'Some Title',
              dueDate: moment(),
              status: 'In Progress'
            }
          ]
        })
      })
    );
  },

  onClickOpenConfirmModal(e) {
    e.preventDefault();
    showModal({
      title: 'Test Confirmation Modal',
      view: new ConfirmationModalView({
        model: new Backbone.Model({
          message: 'Are you sure?'
        }),
        onConfirm() {
          console.log('confirming...');
          return Promise.resolve();
        },
        onConfirmDone() {
          console.log('confirmed!');
        }
      })
    });
  },

  onClickShowAlert(e) {
    e.preventDefault();
    showErrorMessage({
      errorCode: 'general.system.error',
      errorTitle: 'System Error Occurred',
      errorMessage: 'A system error has occurred. Contact site administrator.'
    });
  },

  onClickOpenNewTab(e) {
    e.preventDefault();
    openInNewTab('https://google.com');
  },

  onClickTriggerAjaxError(e) {
    e.preventDefault();
    const spinner = Ladda.create(e.currentTarget);
    spinner.start();
    RevokeTask('foobar')
      .then(() => {
        spinner.stop();
      })
      .catch(() => {
        spinner.stop();
      });
  },

  onClickSubmitParsleyForm(e) {
    e.preventDefault();
    this.ui.parsleyTestForm.parsley().whenValidate();
  },

  onClickMakeReadOnly(e) {
    e.preventDefault();
    makeFormReadOnly(this.ui.parsleyTestForm);
  }
});

export default TestPage;
